import { useEffect, useRef, ReactNode } from 'react';
import { useParticipant, useMediaTrack } from '@daily-co/daily-react';
import { Box, Text } from '@mantine/core';
import { IconMicrophoneOff } from '@tabler/icons-react';

interface ParticipantTileProps {
  sessionId: string;
  controls?: ReactNode;
}

export const ParticipantTile = ({ sessionId, controls }: ParticipantTileProps) => {
  const participant = useParticipant(sessionId);
  const videoRef = useRef<HTMLVideoElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);

  // Get video and audio tracks
  const videoTrack = useMediaTrack(sessionId, 'video');
  const audioTrack = useMediaTrack(sessionId, 'audio');

  // Attach video track
  useEffect(() => {
    if (videoRef.current && videoTrack.persistentTrack) {
      const mediaStream = new MediaStream([videoTrack.persistentTrack]);
      videoRef.current.srcObject = mediaStream;
    }
  }, [videoTrack.persistentTrack]);

  // Attach audio track (only for remote participants)
  useEffect(() => {
    if (audioRef.current && audioTrack.persistentTrack && !participant?.local) {
      const mediaStream = new MediaStream([audioTrack.persistentTrack]);
      audioRef.current.srcObject = mediaStream;
    }
  }, [audioTrack.persistentTrack, participant?.local]);

  if (!participant) {
    return null;
  }

  const isVideoOff = !videoTrack.state || videoTrack.state === 'off' || videoTrack.state === 'blocked';
  const isAudioOff = !audioTrack.state || audioTrack.state === 'off' || audioTrack.state === 'blocked';

  return (
    <Box
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        backgroundColor: '#1a1a1a',
        borderRadius: '12px',
        overflow: 'hidden',
        aspectRatio: '16/9',
      }}
    >
      {/* Video */}
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted={participant.local} // Mute local video to avoid echo
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          display: isVideoOff ? 'none' : 'block',
        }}
      />

      {/* Audio (only for remote participants) */}
      {!participant.local && (
        <audio ref={audioRef} autoPlay playsInline />
      )}

      {/* Placeholder when video is off */}
      {isVideoOff && (
        <Box
          style={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#2a2a2a',
          }}
        >
          <Box
            style={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              backgroundColor: '#667eea',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 32,
              fontWeight: 'bold',
              color: 'white',
            }}
          >
            {participant.user_name?.charAt(0).toUpperCase() || '?'}
          </Box>
        </Box>
      )}

      {/* Microphone muted indicator - top left */}
      {isAudioOff && (
        <Box
          style={{
            position: 'absolute',
            top: 12,
            left: 12,
            zIndex: 10,
            width: 36,
            height: 36,
            borderRadius: '50%',
            backgroundColor: 'rgba(239, 68, 68, 0.9)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
          }}
        >
          <IconMicrophoneOff size={20} color="white" />
        </Box>
      )}

      {/* Overlay with participant info */}
      <Box
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          background: 'linear-gradient(to top, rgba(0,0,0,0.7), transparent)',
          padding: '12px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Text
          size="sm"
          fw={500}
          c="white"
          style={{
            textShadow: '0 2px 4px rgba(0,0,0,0.5)',
          }}
        >
          {participant.user_name || 'Sem nome'}
          {participant.local && ' (Você)'}
        </Text>

        <Box style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          {/* Camera indicator */}
          {isVideoOff && (
            <Box
              style={{
                width: 24,
                height: 24,
                borderRadius: '50%',
                backgroundColor: 'rgba(239, 68, 68, 0.9)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 12,
                color: 'white',
              }}
            >
              📷
            </Box>
          )}

          {/* Speaking indicator */}
          {audioTrack.state === 'playable' && participant.audio && (
            <Box
              style={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: '#10b981',
                animation: 'pulse 2s ease-in-out infinite',
              }}
            />
          )}
        </Box>
      </Box>

      {/* Video controls for local participant */}
      {participant.local && controls && (
        <Box
          style={{
            position: 'absolute',
            bottom: 20,
            right: 20,
            zIndex: 100,
            opacity: 0.5,
            transition: 'opacity 0.3s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.opacity = '1';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.opacity = '0.5';
          }}
        >
          {controls}
        </Box>
      )}
    </Box>
  );
};
